import { useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "@/lib/api";

const HEARTBEAT_MS = 20000; // send a ping every 20s to keep ingress from timing out

/**
 * useSessionSocket
 * Connects to /api/ws/session/{code} and provides live events.
 * onEvent receives every message. Auto-reconnect with backoff.
 * Client sends periodic pings so idle proxies (Kubernetes ingress ~60s) don't drop the connection.
 */
export function useSessionSocket(code, onEvent) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  const heartbeatRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const clearHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const connect = useCallback(() => {
    if (!code) return;
    closedRef.current = false;
    const ws = new WebSocket(WS_URL(code));
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      retryRef.current = 0;
      clearHeartbeat();
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try {
            ws.send("ping");
          } catch (err) {
            console.warn("[ws] heartbeat send failed", err);
          }
        }
      }, HEARTBEAT_MS);
    };

    ws.onmessage = (ev) => {
      // ignore server pongs / plain text keepalives
      if (typeof ev.data === "string" && (ev.data === "pong" || ev.data === "ping")) return;
      try {
        const msg = JSON.parse(ev.data);
        onEventRef.current?.(msg);
      } catch (err) {
        console.warn("[ws] invalid message payload", err);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      clearHeartbeat();
      if (!closedRef.current) {
        const delay = Math.min(8000, 500 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      try {
        ws.close();
      } catch (err) {
        console.warn("[ws] close after error failed", err);
      }
    };
  }, [code]);

  useEffect(() => {
    connect();
    return () => {
      closedRef.current = true;
      clearHeartbeat();
      try {
        wsRef.current?.close();
      } catch (err) {
        console.warn("[ws] cleanup close failed", err);
      }
    };
  }, [connect]);

  return { connected };
}
